import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { Building2, ChevronDown, Check, ArrowLeftRight, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface WorkspaceInfo {
  id: string;
  name: string;
  owner_id: string;
  owner_email?: string;
  plan?: string;
  status?: string;
}

const IMPERSONATED_WORKSPACE_KEY = "superadmin-workspace-id";

export function getImpersonatedWorkspaceId(): string | null {
  return localStorage.getItem(IMPERSONATED_WORKSPACE_KEY);
}

export function setImpersonatedWorkspaceId(id: string | null) {
  if (id) {
    localStorage.setItem(IMPERSONATED_WORKSPACE_KEY, id);
  } else {
    localStorage.removeItem(IMPERSONATED_WORKSPACE_KEY);
  }
  // Dispatch event so other components can react
  window.dispatchEvent(new Event("workspace-changed"));
}

export function WorkspaceSelector() {
  const { isSuperAdmin } = useSuperAdmin();
  const [workspaces, setWorkspaces] = useState<WorkspaceInfo[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(getImpersonatedWorkspaceId());
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchWorkspaces();
  }, [isSuperAdmin]);

  const fetchWorkspaces = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("workspaces")
        .select(`
          id,
          name,
          owner_id,
          subscriptions (
            plan,
            status
          )
        `)
        .order("name");

      if (error) throw error;

      const mapped: WorkspaceInfo[] = (data || []).map((ws: any) => {
        // PostgREST may return subscriptions as an object (1:1) or array
        const sub = Array.isArray(ws.subscriptions)
          ? ws.subscriptions?.[0]
          : ws.subscriptions;
        return {
          id: ws.id,
          name: ws.name,
          owner_id: ws.owner_id,
          plan: sub?.plan || null,
          status: sub?.status || null,
        };
      });

      setWorkspaces(mapped);
    } catch (err) {
      console.error("Error fetching workspaces:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (workspaceId: string | null) => {
    if (workspaceId === selectedId) return;
    setSelectedId(workspaceId);
    setImpersonatedWorkspaceId(workspaceId);
    // Ya no usamos window.location.reload()
    // El setImpersonatedWorkspaceId dispara el evento "workspace-changed"
  };



  if (!isSuperAdmin) return null;

  const selectedWorkspace = workspaces.find((w) => w.id === selectedId);

  const planColors: Record<string, string> = {
    basic: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
    pro: "bg-primary/10 text-primary",
    enterprise: "bg-purple-500/10 text-purple-700 dark:text-purple-400",
    superadmin: "bg-destructive/10 text-destructive",
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 border-dashed border-destructive/50 text-destructive hover:bg-destructive/10 max-w-[220px]"
        >
          <ArrowLeftRight className="h-4 w-4 shrink-0" />
          <span className="truncate text-xs">
            {selectedWorkspace ? selectedWorkspace.name : "Mi Workspace"}
          </span>
          <ChevronDown className="h-3 w-3 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80 max-h-[80vh] overflow-hidden flex flex-col">
        <DropdownMenuLabel className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Workspace
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5"
            onClick={async (e) => {
              e.preventDefault();
              
              // 1. Clear Storage
              localStorage.clear();
              sessionStorage.clear();
              
              // 2. Clear Service Workers
              if ('serviceWorker' in navigator) {
                try {
                  const registrations = await navigator.serviceWorker.getRegistrations();
                  for (const registration of registrations) {
                    await registration.unregister();
                  }
                } catch (err) {
                  console.error("Error unregistering service workers:", err);
                }
              }

              // 3. Clear Cache API
              if ('caches' in window) {
                try {
                  const names = await caches.keys();
                  for (const name of names) {
                    await caches.delete(name);
                  }
                } catch (err) {
                  console.error("Error clearing caches:", err);
                }
              }

              // 4. Clear Cookies
              document.cookie.split(";").forEach((c) => {
                document.cookie = c
                  .replace(/^ +/, "")
                  .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
              });

              toast.success("Caché, persistencia, Service Worker y cookies eliminadas correctamente");
              
              // 5. Force hard reload (bypass browser cache)
              setTimeout(() => {
                // Using a timestamp and force reload if possible
                window.location.href = window.location.origin + window.location.pathname + '?v=' + Date.now();
              }, 1000);
            }}
            title="Vaciar caché y persistencia (LocalStorage)"
          >
            <Trash2 className="h-3.5 w-3.5" />
            <span className="text-[10px] font-bold uppercase">Limpiar</span>
          </Button>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-2 py-1">
          Cambiar Workspace
        </DropdownMenuLabel>
        <div className="px-2 pb-2">
          <Input
            placeholder="Buscar workspace..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-xs"
            autoFocus
          />
        </div>
        <div className="overflow-y-auto max-h-72">
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => handleSelect(null)}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-destructive" />
                <span className="font-medium">Mi Workspace (Superadmin)</span>
              </div>
              {!selectedId && <Check className="h-4 w-4 text-primary" />}
            </div>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {loading ? (
            <div className="p-3 text-center text-sm text-muted-foreground">Cargando...</div>
          ) : (
            workspaces
              .filter((ws) => {
                const matchesSearch = ws.name.toLowerCase().includes(search.toLowerCase());
                const isActive = ws.status === "active" || ws.status === "trialing";
                return matchesSearch && (showInactive ? !isActive : isActive);
              })
              .map((ws) => (
                <DropdownMenuItem
                  key={ws.id}
                  className="cursor-pointer"
                  onClick={() => handleSelect(ws.id)}
                >
                  <div className="flex items-center justify-between w-full gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{ws.name}</p>
                     {ws.plan && (
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge
                            variant="secondary"
                            className={cn("text-[10px] px-1.5 py-0", planColors[ws.plan])}
                          >
                            {ws.plan}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">
                            {ws.status === "active" ? "Activo" : ws.status === "trialing" ? "Prueba" : ws.status || "Sin suscripción"}
                          </span>
                        </div>
                      )}
                    </div>
                    {selectedId === ws.id && <Check className="h-4 w-4 text-primary shrink-0" />}
                  </div>
                </DropdownMenuItem>
              ))
          )}
        </div>
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-muted-foreground"
            onClick={(e) => {
              e.preventDefault();
              setShowInactive(!showInactive);
            }}
          >
            {showInactive ? "Ver activas" : "Ver inactivas"}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
